// File: /js/shared/extensions/deckImport-create.js
//
// Contest Deck Import Phase 2 (2026-05-23) — client orchestrator for the
// self-serve "Importer un deck" control on the contest manager.
//
// The user does ONE action: pick the converted deck folder (the Phase-1
// converter's `out-anim/<deck-slug>/` — `manifest.json` + `slide-NNN/state-KK.png`)
// and click Import. This module then loops the manifest's slides SEQUENTIALLY
// (awaiting each before the next, so the server appends relation positions in
// deck order), POSTing one `importContestSlide` request per slide carrying its
// title + notes + state images. Progress shows in the status span; on completion
// the page reloads so the populated deck appears in the manager.
//
// All user-facing strings come from `data-label-*` attributes server-rendered
// by `AffiliatedChildrenManagerRenderer::renderDeckImportControl` (i18n stays
// in PHP, §26). Listeners are element-scoped (no `document` seam); `setupDeckImport`
// is idempotent and re-runs after each manager-fragment refresh via
// `content-create`'s `setupAll`.

import { csrfToken } from '../utils/config.js';

const ENDPOINT = '/ajax/AjaxCallDispatcher.php';

/** Substitute `{name}` placeholders in a template string. */
function fill(template, params) {
  let out = String(template || '');
  for (const name of Object.keys(params || {})) {
    out = out.split(`{${name}}`).join(String(params[name]));
  }
  return out;
}

/**
 * Build a map of manifest-relative path → File from a webkitdirectory
 * selection. The browser prefixes every `webkitRelativePath` with the picked
 * folder's own name; we strip that first segment so keys match the manifest's
 * paths (`manifest.json`, `slide-001/state-00.png`, …).
 */
function indexSelection(fileList) {
  const map = new Map();
  for (const file of fileList) {
    const rel = file.webkitRelativePath || file.name;
    const slash = rel.indexOf('/');
    const key = slash >= 0 ? rel.slice(slash + 1) : rel;
    map.set(key, file);
  }
  return map;
}

function setStatus(statusEl, text) {
  if (statusEl) statusEl.textContent = text;
}

async function importOneSlide(action, hash, title, notes, stateFiles) {
  const fd = new FormData();
  fd.append('name', action);
  fd.append('csrf_token', csrfToken());
  fd.append('contest_hash', hash);
  fd.append('title', title || '');
  fd.append('notes', notes || '');
  for (const file of stateFiles) {
    fd.append('states[]', file, file.name);
  }

  const res = await fetch(ENDPOINT, { method: 'POST', body: fd });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok || !data || data.success !== true) {
    const message = (data && (data.error || data.details)) || `HTTP ${res.status}`;
    throw new Error(message);
  }
  return data;
}

async function runImport(root) {
  const input    = root.querySelector('[data-deck-import-input="true"]');
  const trigger  = root.querySelector('[data-deck-import-trigger="true"]');
  const statusEl = root.querySelector('[data-deck-import-status="true"]');

  const action = root.dataset.deckImportAction || '';
  const hash   = root.dataset.contestHash || '';
  const labels = {
    reading:    root.dataset.labelReading || '',
    progress:   root.dataset.labelProgress || '',
    done:       root.dataset.labelDone || '',
    error:      root.dataset.labelError || '',
    noManifest: root.dataset.labelNoManifest || '',
    noSlides:   root.dataset.labelNoSlides || '',
    missing:    root.dataset.labelMissing || '',
  };

  const files = input && input.files ? Array.from(input.files) : [];
  if (files.length === 0) return;

  if (trigger) trigger.disabled = true;
  setStatus(statusEl, labels.reading);

  try {
    const selection = indexSelection(files);

    const manifestFile = selection.get('manifest.json');
    if (!manifestFile) {
      setStatus(statusEl, labels.noManifest);
      return;
    }

    let manifest;
    try {
      manifest = JSON.parse(await manifestFile.text());
    } catch {
      setStatus(statusEl, labels.noManifest);
      return;
    }

    const slides = Array.isArray(manifest.slides) ? manifest.slides : [];
    if (slides.length === 0) {
      setStatus(statusEl, labels.noSlides);
      return;
    }

    const total = slides.length;
    for (let i = 0; i < total; i++) {
      const slide = slides[i] || {};
      const position = Number(slide.position) || (i + 1);
      const statePaths = Array.isArray(slide.states) ? slide.states : [];

      const stateFiles = [];
      for (const rel of statePaths) {
        const file = selection.get(String(rel));
        if (!file) {
          setStatus(statusEl, fill(labels.missing, { path: rel }));
          return;
        }
        stateFiles.push(file);
      }

      setStatus(statusEl, fill(labels.progress, { done: i, total }));

      try {
        await importOneSlide(action, hash, slide.title || '', slide.notes || '', stateFiles);
      } catch (err) {
        setStatus(statusEl, fill(labels.error, { position, error: err.message }));
        return;
      }
    }

    setStatus(statusEl, fill(labels.done, { count: total }));
    // Reload so the freshly-appended slides render in the manager deck.
    window.setTimeout(() => window.location.reload(), 1200);
  } finally {
    if (trigger) trigger.disabled = false;
  }
}

export function setupDeckImport() {
  const roots = document.querySelectorAll('[data-deck-import="true"]');
  roots.forEach((root) => {
    if (root.dataset.deckImportBound === '1') return;
    root.dataset.deckImportBound = '1';

    const input   = root.querySelector('[data-deck-import-input="true"]');
    const trigger = root.querySelector('[data-deck-import-trigger="true"]');
    if (!input || !trigger) return;

    trigger.addEventListener('click', () => input.click());
    input.addEventListener('change', () => { runImport(root); });
  });
}
